import { useRef, useState } from 'react'
import {
  CheckCircle2,
  Download,
  FileText,
  History,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'

import {
  useActivateMapPolicy,
  useCreateMapPolicy,
  useCreateMapPolicyTier,
  useDeleteMapPolicy,
  useDeleteMapPolicyTier,
  useDeletePolicyDocument,
  usePolicyDocuments,
  useSetCurrentPolicyDocument,
  useUpdateMapPolicy,
  useUpdateMapPolicyTier,
  useUploadPolicyDocument,
} from '@/api/hooks'
import type { components } from '@/api/schema'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ActionPopover } from '@/components/action-popover'
import {
  InlineFieldGroup,
  InlineFieldInput,
  InlineFieldTextarea,
  type InlineGroupField,
} from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getApiErrorMessage } from '@/lib/api-error'

type Policy = components['schemas']['ManufacturerMapPolicyDetailRead']
type PolicyType = 'flat_percent' | 'tiered' | 'fixed_price' | 'hybrid'
type Tier = components['schemas']['ManufacturerMapTierRead']

const POLICY_TYPE_LABELS: Record<PolicyType, string> = {
  flat_percent: 'Flat % of LIST',
  tiered: 'Tiered (per product line)',
  fixed_price: 'Fixed-price schedule',
  hybrid: 'Hybrid (formula + overrides)',
}

const STATUS_COLOR: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  draft: 'secondary',
  expired: 'destructive',
}

function extractErrorDetail(err: unknown): string {
  return getApiErrorMessage(err, 'Request failed')
}

/** Live preview: "$1000 LIST → $X MAP" given a chain string. */
function chainPreview(listPrice: number, chain: string | null | undefined): string {
  if (!chain) return '—'
  const segments = chain.split('/').map((s) => s.trim())
  let running = listPrice
  for (const seg of segments) {
    const n = Number(seg)
    if (!Number.isFinite(n) || n < 0 || n > 100) return 'Invalid chain'
    running = running * ((100 - n) / 100)
  }
  return `$${listPrice.toLocaleString()} LIST → $${running.toFixed(2)} MAP`
}

// -----------------------------------------------------------------------------
// Empty state — Create policy dialog
// -----------------------------------------------------------------------------

function CreatePolicyDialog({ manuUuid }: { manuUuid: string }) {
  const [open, setOpen] = useState(false)
  const [policyType, setPolicyType] = useState<PolicyType>('flat_percent')
  const [policyLabel, setPolicyLabel] = useState('MAP')
  const [chain, setChain] = useState('')
  const [notes, setNotes] = useState('')
  const createMutation = useCreateMapPolicy(manuUuid)

  function submit() {
    const body = {
      policy_type: policyType,
      policy_label: policyLabel || 'MAP',
      default_discount_chain: chain.trim() || null,
      notes: notes.trim() || null,
    }
    createMutation.mutate(body, {
      onSuccess: () => {
        toast.success('MAP policy created as draft')
        setOpen(false)
      },
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Create MAP Policy
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create MAP policy</DialogTitle>
          <DialogDescription>
            Starts as a draft. Activate after reviewing the chain on a sample of products.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Policy type</Label>
            <Select value={policyType} onValueChange={(v) => setPolicyType(v as PolicyType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(POLICY_TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Label</Label>
            <InlineFieldInput
              value={policyLabel}
              onChange={(e) => setPolicyLabel(e.target.value)}
              placeholder="MAP, UMRP, IMAP, MRICP..."
            />
          </div>
          {policyType !== 'tiered' && policyType !== 'fixed_price' && (
            <div className="space-y-1">
              <Label>Default discount chain</Label>
              <InlineFieldInput
                value={chain}
                onChange={(e) => setChain(e.target.value)}
                placeholder='e.g. "35" or "50/10/21/15"'
              />
              <p className="text-xs text-muted-foreground">
                {chain ? chainPreview(1000, chain) : 'Type a chain to preview the math'}
              </p>
            </div>
          )}
          <div className="space-y-1">
            <Label>Notes (optional)</Label>
            <InlineFieldTextarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Source PDF reference, key-account carve-outs, etc."
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <LoadingButton onClick={submit} loading={createMutation.isPending} loadingText="Creating…">
            Create draft
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// -----------------------------------------------------------------------------
// Policy fields, edited in place on the summary card (USA-1580)
// -----------------------------------------------------------------------------

type PolicyFieldKey = 'type' | 'label' | 'chain' | 'notes'

const POLICY_FIELDS: ReadonlyArray<InlineGroupField<PolicyFieldKey>> = [
  {
    key: 'type',
    label: 'Type',
    kind: 'select',
    options: Object.entries(POLICY_TYPE_LABELS).map(([value, label]) => ({ value, label })),
    className: 'col-span-6 sm:col-span-3',
  },
  { key: 'label', label: 'Label', className: 'col-span-6 sm:col-span-3' },
  {
    key: 'chain',
    label: 'Default discount chain',
    hidden: (draft) => draft.type === 'tiered' || draft.type === 'fixed_price',
  },
  { key: 'notes', label: 'Notes', kind: 'textarea' },
]

function PolicyDetails({ manuUuid, policy }: { manuUuid: string; policy: Policy }) {
  const [editing, setEditing] = useState(false)
  const update = useUpdateMapPolicy(manuUuid)
  const policyTypeLabel = POLICY_TYPE_LABELS[policy.policy_type as PolicyType] ?? policy.policy_type
  const statusVariant = STATUS_COLOR[policy.status] ?? 'outline'

  function submit(next: Record<PolicyFieldKey, string>) {
    update.mutate(
      {
        policyUuid: policy.uuid,
        body: {
          policy_type: next.type as PolicyType,
          policy_label: next.label,
          default_discount_chain: next.chain.trim() || null,
          notes: next.notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Policy updated')
          setEditing(false)
        },
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  return (
    <InlineFieldGroup
      label="MAP policy"
      fields={POLICY_FIELDS}
      values={{
        type: policy.policy_type,
        label: policy.policy_label,
        chain: policy.default_discount_chain ?? '',
        notes: policy.notes ?? '',
      }}
      notice={(draft) =>
        draft.type === 'tiered' || draft.type === 'fixed_price'
          ? null
          : draft.chain.trim()
            ? chainPreview(1000, draft.chain)
            : 'Discount chains read like "35" or "50/10/21/15".'
      }
      pending={update.isPending}
      editing={editing}
      onEditingChange={setEditing}
      onSave={submit}
    >
      <span className="block space-y-1">
        <span className="flex items-center gap-2">
          <span className="text-base font-semibold">{policy.policy_label}</span>
          <Badge variant={statusVariant}>{policy.status}</Badge>
          <Badge variant="outline">{policy.enforcement_stage}</Badge>
        </span>
        <span className="block text-sm text-muted-foreground">{policyTypeLabel}</span>
        {policy.policy_type !== 'tiered' && policy.policy_type !== 'fixed_price' && (
          <span className="mt-3 block rounded bg-muted/40 p-3 text-sm">
            <span className="font-medium">Default chain:</span>{' '}
            <code>{policy.default_discount_chain ?? '—'}</code>
            <span className="ml-2 text-muted-foreground">
              {chainPreview(1000, policy.default_discount_chain)}
            </span>
          </span>
        )}
        <span className="mt-3 block whitespace-pre-wrap text-sm text-muted-foreground">
          {policy.notes || 'No notes'}
        </span>
      </span>
    </InlineFieldGroup>
  )
}

// -----------------------------------------------------------------------------
// Tier row + add-tier dialog
// -----------------------------------------------------------------------------

function TierRow({ manuUuid, tier }: { manuUuid: string; tier: Tier }) {
  const update = useUpdateMapPolicyTier(manuUuid)
  const del = useDeleteMapPolicyTier(manuUuid)

  function submit(change: Partial<{ name: string; discount_chain: string; is_default: boolean }>) {
    update.mutate(
      {
        tierUuid: tier.uuid,
        body: {
          name: tier.name,
          discount_chain: tier.discount_chain,
          is_default: tier.is_default,
          ...change,
        },
      },
      {
        onSuccess: () => toast.success('Tier updated'),
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  function remove() {
    del.mutate(tier.uuid, {
      onSuccess: () => toast.success('Tier removed'),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  return (
    <TableRow>
      <TableCell className="font-medium">
        <POInlineEditableField
          value={tier.name}
          ariaLabel={`Tier name ${tier.name}`}
          buttonClassName="-ml-2 font-medium"
          pending={update.isPending}
          showEditIcon
          onConfirm={(name) => submit({ name })}
        />
      </TableCell>
      <TableCell>
        <POInlineEditableField
          value={tier.discount_chain}
          ariaLabel={`Discount chain for ${tier.name}`}
          buttonClassName="-ml-2"
          pending={update.isPending}
          showEditIcon
          onConfirm={(discount_chain) => submit({ discount_chain })}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={tier.is_default}
          disabled={update.isPending}
          aria-label={`Default tier: ${tier.name}`}
          title="Default tier (used when no explicit assignment)"
          onCheckedChange={(is_default) => submit({ is_default })}
        />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{chainPreview(1000, tier.discount_chain)}</TableCell>
      <TableCell className="text-right">
        <ActionPopover
          align="end"
          trigger={
            <LoadingButton variant="ghost" size="icon" loading={del.isPending}>
              <Trash2 className="h-4 w-4" />
            </LoadingButton>
          }
          title={`Delete tier "${tier.name}"?`}
          confirmLabel="Delete"
          confirmVariant="destructive"
          onConfirm={(_note, close) => {
            close()
            remove()
          }}
        />
      </TableCell>
    </TableRow>
  )
}

function AddTierDialog({ manuUuid, policyUuid }: { manuUuid: string; policyUuid: string }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [chain, setChain] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const create = useCreateMapPolicyTier(manuUuid)

  function submit() {
    create.mutate(
      {
        policyUuid,
        body: { name, discount_chain: chain, is_default: isDefault },
      },
      {
        onSuccess: () => {
          toast.success('Tier added')
          setOpen(false)
          setName('')
          setChain('')
          setIsDefault(false)
        },
        onError: (err) => toast.error(extractErrorDetail(err)),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus className="h-4 w-4" />
          Add tier
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add tier</DialogTitle>
          <DialogDescription>
            Products are assigned to tiers individually on the product detail page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Tier name</Label>
            <InlineFieldInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Premier Bar"'
            />
          </div>
          <div className="space-y-1">
            <Label>Discount chain</Label>
            <InlineFieldInput
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              placeholder='e.g. "50/5"'
            />
            <p className="text-xs text-muted-foreground">
              {chain ? chainPreview(1000, chain) : '—'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} id="add-default" />
            <Label htmlFor="add-default" className="cursor-pointer">
              Default tier (used when a product has no explicit assignment)
            </Label>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <LoadingButton onClick={submit} loading={create.isPending} loadingText="Adding…">
            Add tier
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// -----------------------------------------------------------------------------
// Main tab
// -----------------------------------------------------------------------------

export function MapPolicyTab({
  manuUuid,
  policy,
}: {
  manuUuid: string
  policy: Policy | null | undefined
}) {
  const activate = useActivateMapPolicy(manuUuid)
  const del = useDeleteMapPolicy(manuUuid)

  if (!policy) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center">
        <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          No MAP policy configured for this manufacturer.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          The legacy <code>list_price_map_multiplier</code> fallback (if set) still applies
          until a policy is added.
        </p>
        <div className="mt-4">
          <CreatePolicyDialog manuUuid={manuUuid} />
        </div>
      </div>
    )
  }

  function activatePolicy() {
    activate.mutate(policy!.uuid, {
      onSuccess: () => toast.success('Policy activated — sync will pick it up on next run'),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  function deletePolicy() {
    del.mutate(policy!.uuid, {
      onSuccess: () => toast.success('Policy deleted'),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <PolicyDetails manuUuid={manuUuid} policy={policy} />
          </div>
          <div className="flex flex-wrap gap-2">
            {policy.status !== 'active' && (
              <LoadingButton
                onClick={activatePolicy}
                loading={activate.isPending}
                loadingText="Activating…"
                size="sm"
              >
                <CheckCircle2 className="h-4 w-4" />
                Activate
              </LoadingButton>
            )}
            <ActionPopover
              align="end"
              trigger={
                <LoadingButton variant="outline" size="sm" loading={del.isPending}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </LoadingButton>
              }
              title="Delete this MAP policy?"
              confirmLabel="Delete"
              confirmVariant="destructive"
              onConfirm={(_note, close) => {
                close()
                deletePolicy()
              }}
            >
              <p className="text-xs text-muted-foreground">
                Tiers and product overrides will be removed too.
              </p>
            </ActionPopover>
          </div>
        </div>
      </div>

      {/* Tiers section */}
      {policy.policy_type === 'tiered' && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold">Tiers ({policy.tiers.length})</h4>
            <AddTierDialog manuUuid={manuUuid} policyUuid={policy.uuid} />
          </div>
          {policy.tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tiers yet — add at least one to activate this policy.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Chain</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Preview ($1000 LIST)</TableHead>
                  <TableHead className="w-16 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policy.tiers.map((t) => (
                  <TierRow key={t.id} manuUuid={manuUuid} tier={t} />
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Overrides summary (read-only — managed from product detail) */}
      {(policy.policy_type === 'fixed_price' || policy.policy_type === 'hybrid') && (
        <div className="rounded-lg border bg-card p-4">
          <h4 className="mb-2 text-sm font-semibold">
            Per-product overrides ({policy.overrides.length})
          </h4>
          <p className="text-xs text-muted-foreground">
            Manage per-product MAP overrides from the product detail page, or via the xlsx
            importer (coming in Phase 3).
          </p>
        </div>
      )}

      {/* Policy documents (always shown — independent of policy state) */}
      <PolicyDocumentsSection manuUuid={manuUuid} />
    </div>
  )
}

// -----------------------------------------------------------------------------
// Policy documents section — list, upload, set-current, download, delete
// -----------------------------------------------------------------------------

type PolicyDoc = components['schemas']['ManufacturerPolicyDocumentRead']

function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatUploadedAt(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function PolicyDocumentsSection({ manuUuid }: { manuUuid: string }) {
  const { data: docs, isLoading } = usePolicyDocuments(manuUuid)
  const uploadMutation = useUploadPolicyDocument(manuUuid)
  const setCurrentMutation = useSetCurrentPolicyDocument(manuUuid)
  const deleteMutation = useDeletePolicyDocument(manuUuid)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    uploadMutation.mutate(
      { file },
      {
        onSuccess: (doc) => {
          toast.success(`Uploaded ${doc.file_name}${doc.is_current ? ' (now current)' : ''}`)
          if (fileInputRef.current) fileInputRef.current.value = ''
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Upload failed')),
      },
    )
  }

  function promoteToCurrent(doc: PolicyDoc) {
    setCurrentMutation.mutate(doc.uuid, {
      onSuccess: () => toast.success(`Promoted ${doc.file_name} to current`),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  function deleteDoc(doc: PolicyDoc) {
    deleteMutation.mutate(doc.uuid, {
      onSuccess: () => toast.success('Document deleted'),
      onError: (err) => toast.error(extractErrorDetail(err)),
    })
  }

  const sorted = (docs ?? []).slice().sort((a, b) => {
    if (a.is_current && !b.is_current) return -1
    if (!a.is_current && b.is_current) return 1
    return (b.uploaded_at ?? '').localeCompare(a.uploaded_at ?? '')
  })
  const current = sorted.find((d) => d.is_current)
  const historical = sorted.filter((d) => !d.is_current)

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <FileText className="h-4 w-4" />
          Policy documents{docs ? ` (${docs.length})` : ''}
        </h4>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.xlsx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleUpload}
            className="hidden"
            id={`policy-doc-upload-${manuUuid}`}
          />
          <LoadingButton
            size="sm"
            loading={uploadMutation.isPending}
            loadingText="Uploading…"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            Upload document
          </LoadingButton>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No policy documents on file. Upload a PDF, docx, or xlsx — it becomes the current
          doc, and replaces/demotes any prior current to historical.
        </p>
      ) : (
        <>
          {current && (
            <div className="mb-3">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Current
              </p>
              <DocRow
                doc={current}
                onPromote={null}
                onDelete={() => deleteDoc(current)}
                deleting={deleteMutation.isPending && deleteMutation.variables === current.uuid}
              />
            </div>
          )}
          {historical.length > 0 && (
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <History className="h-3.5 w-3.5" />
                History ({historical.length})
              </p>
              <div className="space-y-1.5">
                {historical.map((doc) => (
                  <DocRow
                    key={doc.uuid}
                    doc={doc}
                    onPromote={() => promoteToCurrent(doc)}
                    onDelete={() => deleteDoc(doc)}
                    deleting={deleteMutation.isPending && deleteMutation.variables === doc.uuid}
                    promoting={
                      setCurrentMutation.isPending && setCurrentMutation.variables === doc.uuid
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function DocRow({
  doc,
  onPromote,
  onDelete,
  deleting,
  promoting,
}: {
  doc: PolicyDoc
  onPromote: (() => void) | null
  onDelete: () => void
  deleting?: boolean
  promoting?: boolean
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-md border bg-background p-3 ${
        doc.is_current ? 'border-primary/40' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{doc.file_name}</p>
        <p className="text-xs text-muted-foreground">
          {formatUploadedAt(doc.uploaded_at)} · {formatFileSize(doc.size_bytes)} ·{' '}
          <span className="font-mono text-[10px]">{doc.sha256?.slice(0, 12)}</span>
        </p>
        {doc.notes && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{doc.notes}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {doc.is_current && <Badge variant="default">Current</Badge>}
        {doc.download_url && (
          <Button
            size="icon"
            variant="ghost"
            title="Download"
            onClick={() => window.open(doc.download_url!, '_blank', 'noopener')}
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
        {onPromote && (
          <LoadingButton
            size="sm"
            variant="outline"
            loading={!!promoting}
            onClick={onPromote}
          >
            Set current
          </LoadingButton>
        )}
        <ActionPopover
          align="end"
          trigger={
            <LoadingButton size="icon" variant="ghost" loading={!!deleting}>
              <Trash2 className="h-4 w-4" />
            </LoadingButton>
          }
          title={`Delete document ${doc.file_name}?`}
          confirmLabel="Delete"
          confirmVariant="destructive"
          onConfirm={(_note, close) => {
            close()
            onDelete()
          }}
        >
          <p className="text-xs text-muted-foreground">S3 object retained for audit.</p>
        </ActionPopover>
      </div>
    </div>
  )
}
